import { FileText, Trophy } from "lucide-react";
import { sideMeta } from "./constants";
import NumberField from "./NumberField";
import Avatar from "@/components/Avatar";

function OfficialCaptainPicker({ match, sheet, isCoach, onChange }) {
  const availables = (match.availabilities || [])
    .filter((a) => a.status === "available")
    .map((a) => (match.players || []).find((p) => p.id === a.player_id))
    .filter(Boolean);
  return (
    <div data-testid="official-captain-picker" className="max-w-md">
      <label className="block text-xs font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-2">
        Capitaine
      </label>
      <select
        data-testid="team-captain-select"
        disabled={!isCoach}
        value={sheet.team_captain_id || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white disabled:opacity-70"
      >
        <option value="">— Aucun —</option>
        {availables.map((p) => (
          <option key={p.id} value={p.id}>
            {p.jersey_number ? `#${p.jersey_number} ` : ""}
            {p.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function StatRow({ id, jersey, name, position, st, isCoach, updateStat, player }) {
  return (
    <div
      data-testid={`stat-row-${id}`}
      className="p-4 rounded-lg bg-slate-800 border border-white/5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {player ? (
            <Avatar player={player} size={40} />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center font-mono-stat font-bold text-sm">
              {jersey || "-"}
            </div>
          )}
          <div>
            <div className="font-bold text-sm flex items-center gap-1.5">
              {name}
              {jersey != null && (
                <span className="text-[10px] font-mono-stat text-slate-500">#{jersey}</span>
              )}
            </div>
            <div className="text-xs text-slate-400">{position || "—"}</div>
          </div>
        </div>
        {isCoach && (
          <label className="flex items-center gap-2 text-xs uppercase tracking-wide no-print">
            <input
              data-testid={`played-${id}`}
              type="checkbox"
              checked={!!st.played}
              onChange={(e) => updateStat(id, "played", e.target.checked)}
              className="w-4 h-4 accent-[#E5B85C]"
            />
            Joué
          </label>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <NumberField
          label="Buts marqués"
          disabled={!isCoach}
          testid={`goals-${id}`}
          value={st.goals}
          onChange={(v) => updateStat(id, "goals", v)}
          color="#E5B85C"
        />
        <NumberField
          label="Assists"
          disabled={!isCoach}
          testid={`assists-${id}`}
          value={st.assists}
          onChange={(v) => updateStat(id, "assists", v)}
          color="#60A5FA"
        />
        <NumberField
          label="Cartons jaunes"
          disabled={!isCoach}
          testid={`yellow-${id}`}
          value={st.yellow_cards}
          onChange={(v) => updateStat(id, "yellow_cards", v)}
          color="#EAB308"
        />
        <NumberField
          label="Cartons rouges"
          disabled={!isCoach}
          testid={`red-${id}`}
          value={st.red_cards}
          onChange={(v) => updateStat(id, "red_cards", v)}
          color="#EF4444"
        />
      </div>
      <input
        data-testid={`observation-${id}`}
        type="text"
        disabled={!isCoach}
        value={st.observation || ""}
        onChange={(e) => updateStat(id, "observation", e.target.value)}
        className="mt-3 w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-sm text-white disabled:opacity-70"
        placeholder="Observation du coach..."
      />
    </div>
  );
}

export default function StatsTab({ match, sheet, setSheet, isCoach, updateStat, submitSheet }) {
  const isOfficial = match.match_type === "official";
  const bleus = (match.rosters || []).filter((r) => r.side === "bleus");
  const rouges = (match.rosters || []).filter((r) => r.side === "rouges");

  return (
    <div className="rounded-2xl bg-slate-900 border border-white/10 p-5 sm:p-6 space-y-6">
      {!isOfficial && (
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-sky-400 mb-2">
              Score Bleus
            </label>
            <input
              data-testid="score-bleus-input"
              type="number"
              min="0"
              disabled={!isCoach}
              value={sheet.score_bleus}
              onChange={(e) => setSheet({ ...sheet, score_bleus: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg font-mono-stat font-bold text-3xl text-center text-sky-400 disabled:opacity-70"
            />
          </div>
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-rose-400 mb-2">
              Score Rouges
            </label>
            <input
              data-testid="score-rouges-input"
              type="number"
              min="0"
              disabled={!isCoach}
              value={sheet.score_rouges}
              onChange={(e) => setSheet({ ...sheet, score_rouges: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg font-mono-stat font-bold text-3xl text-center text-rose-400 disabled:opacity-70"
            />
          </div>
        </div>
      )}

      {isOfficial && (
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-2">
              Notre score
            </label>
            <input
              data-testid="our-score-input"
              type="number"
              min="0"
              disabled={!isCoach}
              value={sheet.our_score}
              onChange={(e) => setSheet({ ...sheet, our_score: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg font-mono-stat font-bold text-3xl text-center text-[#E5B85C] disabled:opacity-70"
            />
          </div>
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-300 mb-2 truncate">
              {match.opponent_name || "Adversaire"}
            </label>
            <input
              data-testid="opponent-score-input"
              type="number"
              min="0"
              disabled={!isCoach}
              value={sheet.opponent_score}
              onChange={(e) => setSheet({ ...sheet, opponent_score: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg font-mono-stat font-bold text-3xl text-center text-slate-200 disabled:opacity-70"
            />
          </div>
        </div>
      )}

      {isOfficial && (
        <OfficialCaptainPicker
          match={match}
          sheet={sheet}
          isCoach={isCoach}
          onChange={(pid) => setSheet({ ...sheet, team_captain_id: pid })}
        />
      )}

      <div>
        <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
          Résumé du match
        </label>
        <textarea
          data-testid="match-summary-input"
          disabled={!isCoach}
          value={sheet.summary}
          onChange={(e) => setSheet({ ...sheet, summary: e.target.value })}
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white h-24 resize-none disabled:opacity-70"
          placeholder="Débrief..."
        />
      </div>

      <div>
        <label className="block text-xs font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-2 flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5" /> Homme du match
        </label>
        <select
          data-testid="motm-select"
          disabled={!isCoach}
          value={sheet.man_of_the_match || ""}
          onChange={(e) => setSheet({ ...sheet, man_of_the_match: e.target.value || null })}
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white disabled:opacity-70"
        >
          <option value="">— Aucun —</option>
          {isOfficial
            ? Object.keys(sheet.stats).map((pid) => {
                const p = (match.players || []).find((pp) => pp.id === pid);
                return (
                  <option key={pid} value={pid}>
                    {p?.name || "Joueur"}
                  </option>
                );
              })
            : (match.rosters || []).map((r) => (
                <option key={r.player_id} value={r.player_id}>
                  {r.player_name} ({r.side === "bleus" ? "Bleus" : "Rouges"})
                </option>
              ))}
        </select>
      </div>

      {isOfficial ? (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-3 h-3 rounded-full bg-[#E5B85C]" />
            <div className="font-display font-bold text-lg uppercase">
              Notre équipe · vs {match.opponent_name || "Adversaire"}
            </div>
            <span className="text-xs font-mono-stat text-slate-500">
              · {Object.keys(sheet.stats).length} joueur(s)
            </span>
          </div>
          <div className="space-y-3">
            {Object.keys(sheet.stats).length === 0 && (
              <div className="text-sm text-slate-500 p-4 rounded-lg border border-dashed border-white/10 text-center">
                Aucun joueur présent. Passe d'abord par l'onglet Présence.
              </div>
            )}
            {Object.entries(sheet.stats).map(([pid, st]) => {
              const p = (match.players || []).find((pp) => pp.id === pid);
              if (!p) return null;
              return (
                <StatRow
                  key={pid}
                  id={pid}
                  jersey={p.jersey_number}
                  name={p.name}
                  position={p.position}
                  st={st}
                  isCoach={isCoach}
                  updateStat={updateStat}
                  player={p}
                />
              );
            })}
          </div>
        </div>
      ) : (
        ["bleus", "rouges"].map((side) => {
          const list = side === "bleus" ? bleus : rouges;
          return (
            <div key={side}>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ background: sideMeta[side].color }}
                />
                <div className="font-display font-bold text-lg uppercase">
                  {sideMeta[side].label}
                </div>
                <span className="text-xs font-mono-stat text-slate-500">
                  · {list.length} joueur(s)
                </span>
              </div>
              <div className="space-y-3">
                {list.length === 0 && (
                  <div className="text-sm text-slate-500 p-4 rounded-lg border border-dashed border-white/10 text-center">
                    Aucun joueur assigné. Va dans l'onglet Répartition.
                  </div>
                )}
                {list.map((r) => {
                  const p = (match.players || []).find((pp) => pp.id === r.player_id);
                  const st = sheet.stats[r.player_id] || { side };
                  return (
                    <StatRow
                      key={r.player_id}
                      id={r.player_id}
                      jersey={p?.jersey_number}
                      name={r.player_name}
                      position={p?.position}
                      st={st}
                      isCoach={isCoach}
                      updateStat={updateStat}
                      player={p}
                    />
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {isCoach && (
        <div className="no-print flex justify-end pt-2">
          <button
            data-testid="save-sheet-button"
            onClick={submitSheet}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg"
          >
            <FileText className="w-4 h-4" /> Enregistrer la feuille
          </button>
        </div>
      )}
    </div>
  );
}
